import gsap from "gsap";
import gql from "graphql-tag";
import className from "classnames/bind";
import { useEffect, useMemo, useRef, useState } from "react";
import { useInView, AnimatePresence, motion } from "motion/react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { PressReleases } from "@/queries/PressReleases";
import { easeOutQuad } from "@/core/easing";
import { AnimVals } from "@/utils/animUtils";
import { useViewSize } from "@/providers/ViewSizeContext";
import ListingPressReleasesItem from "./ListingPressReleasesItem";
import { useHeader } from "@/providers/HeaderContext";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import {
  Container,
  MaterialLoader,
  QueryPagination,
  RevealWrapper,
  SectionHeader,
} from "@components";
import {
  ListingPressReleasesFragment,
  PressReleasesQuery,
} from "@graphqlTypes";
import { useClientMount } from "@/providers/ClientMountProvider";

import styles from "./ListingPressReleases.module.scss";
const cx = className.bind(styles);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.085,
      delayChildren: 0,
    }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.2, ease: easeOutQuad }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: easeOutQuad }
  },
  exit: { opacity: 0 }
};

type ListingPressReleasesProps = ListingPressReleasesFragment & {
  initialPressReleases?: PressReleasesQuery["pressReleases"];
};

export default function ListingPressReleases({
  headline,
  initialPressReleases,
}: ListingPressReleasesProps) {
  const PER_PAGE = 12;
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const { mounted } = useClientMount();
  const { currentLanguage, setIgnoreScroll, headerRef, headerVisible } = useHeader();
  const [getPressReleases] = useLazyQuery<PressReleasesQuery>(PressReleases);
  const { isDesktop, isReady } = useViewSize();
  const rootEl = useRef<HTMLDivElement>(null);
  const headerVisibleRef = useRef(headerVisible);
  const contentRef = useRef<HTMLDivElement>(null);
  const [pressReleases, setPressReleases] = useState(initialPressReleases?.nodes || []);

  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  const showPaging = useMemo(
    () => initialPressReleases?.pageInfo.offsetPagination.hasMore,
    [initialPressReleases]
  );
  const totalCount = useMemo(
    () => initialPressReleases?.pageInfo.offsetPagination.total || 0,
    [initialPressReleases]
  );

  const scrollToContent = () => {
    if (!mounted || !isReady) return;
    
    let offset = 24;
    if (headerVisibleRef.current && headerRef.current) offset += headerRef.current.offsetHeight;
    setIgnoreScroll(true);
    gsap.to(window, {
      scrollTo: { y: contentRef.current, offsetY: offset },
      onComplete: () => {
        setTimeout(() => { setIgnoreScroll(false); }, 150);
      }
    });
  };

  const fetchPressReleases = async (page: number) => {
    setLoading(true);
    setCurrentPage(page);
    scrollToContent();
    const { data } = await getPressReleases({
      variables: {
        size: PER_PAGE,
        offset: (page - 1) * PER_PAGE,
        language: currentLanguage
      },
    });
    if (data?.pressReleases.nodes) setPressReleases(data.pressReleases.nodes);
    else setPressReleases([]);
    setLoading(false);
  };

  useEffect(() => {
    if (!mounted || !isReady) return;
    gsap.registerPlugin(ScrollToPlugin);
  }, [mounted, isReady]);
  
  useEffect(() => {
    headerVisibleRef.current = headerVisible;
  }, [headerVisible]);

  if (!pressReleases.length) return null;

  return (
    <section className={cx(["listing-press-releases"])} ref={rootEl}>
      <Container>
        <RevealWrapper isInView={isInView}>
          <SectionHeader headline={headline} isInView={isInView} />
          <div className={cx("listing-press-releases__items")} ref={contentRef}>
            <AnimatePresence>
              {loading && (
                <motion.div className={cx("listing-press-releases__loading")}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <MaterialLoader className={cx("listing-press-releases__loading__loader")} />
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence initial={false} mode="wait">
              <motion.div
                key={pressReleases.map(item => item.uri).join('-')}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className={cx("listing-press-releases__items-wrapper")}
              >
                {pressReleases.map((item) => (
                  <ListingPressReleasesItem key={item.uri} variants={itemVariants} {...item}/>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
          {showPaging && (
            <QueryPagination
              className={cx("listing-press-releases__pagination")}
              totalCount={totalCount}
              perPage={PER_PAGE}
              size="small"
              currentPage={currentPage}
              onPageChange={fetchPressReleases}
            />
          )}
        </RevealWrapper>
      </Container>
    </section>
  );
}

ListingPressReleases.fragments = {
  entry: gql`
    fragment ListingPressReleases on FlexiblePageContentContentListingPressReleasesLayout {
      headline
    }
  `,
};
